import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Check, Upload, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const CandidatureHSActionModal = ({ isOpen, onClose, onSuccess, candidature, actionType }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    raison_refus: ''
  });
  const [hsDocuments, setHsDocuments] = useState({
    piece_identite: null,
    attestation_secu: null,
    justificatif_domicile: null,
    rib: null
  });

  if (!isOpen || !candidature) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (type, file) => {
    if (!file) {
      setHsDocuments(prev => ({
        ...prev,
        [type]: null
      }));
      return;
    }

    // Validation du format de fichier
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format de fichier non autorisé. Fichiers acceptés : PDF, DOC, DOCX, JPG, PNG, WEBP.');
      return;
    }

    // Validation de la taille (10 Mo max)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      toast.error('Le fichier est trop volumineux. Taille maximum autorisée : 10 Mo.');
      return;
    }

    setHsDocuments(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.ok) {
      const result = await response.json();
      return result.filename;
    } else {
      throw new Error('Erreur lors de l\'upload du fichier');
    }
  };

  const handleSubmit = async () => {
    if (actionType === 'accepte') {
      // For HS acceptance, just change status - documents will be handled in visualization modal
      await updateCandidatureStatus('accepte');
    } else if (actionType === 'refuse') {
      await updateCandidatureStatus('refuse');
    } else if (actionType === 'visualiser') {
      // Handle document uploads for HS
      await handleDocumentUploads();
    }
  };

  const updateCandidatureStatus = async (status) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        status: status,
        ...(status === 'refuse' && formData.raison_refus.trim() && {
          raison_refus: formData.raison_refus.trim()
        })
      };

      const response = await fetch(`${API}/candidatures/${candidature.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(status === 'accepte' ? 'Candidature acceptée' : 'Candidature refusée');
        onSuccess();
      } else {
        toast.error('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Error updating candidature status:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUploads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {};

      // Upload each document if provided
      if (hsDocuments.piece_identite) {
        const filename = await uploadFile(hsDocuments.piece_identite);
        updateData.piece_identite_filename = filename;
      }
      if (hsDocuments.attestation_secu) {
        const filename = await uploadFile(hsDocuments.attestation_secu);
        updateData.attestation_secu_filename = filename;
      }
      if (hsDocuments.justificatif_domicile) {
        const filename = await uploadFile(hsDocuments.justificatif_domicile);
        updateData.justificatif_domicile_filename = filename;
      }
      if (hsDocuments.rib) {
        const filename = await uploadFile(hsDocuments.rib);
        updateData.rib_filename = filename;
      }

      // Update candidature with new document filenames
      if (Object.keys(updateData).length > 0) {
        const response = await fetch(`${API}/candidatures/${candidature.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...updateData,
            updated_at: new Date().toISOString()
          }),
        });

        if (response.ok) {
          toast.success('Documents ajoutés avec succès');
          onSuccess();
        } else {
          toast.error('Erreur lors de l\'ajout des documents');
        }
      } else {
        toast.info('Aucun document à ajouter');
        onClose();
      }
    } catch (error) {
      console.error('Error uploading HS documents:', error);
      toast.error('Erreur lors de l\'upload des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${filename}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erreur de connexion lors du téléchargement');
    }
  };

  const getModalTitle = () => {
    switch (actionType) {
      case 'accepte':
        return 'Accepter la candidature';
      case 'refuse':
        return 'Refuser la candidature';
      case 'visualiser':
        return 'Détails candidature acceptée - Documents supplémentaires';
      default:
        return 'Action candidature';
    }
  };

  const documentLabels = {
    piece_identite: 'Pièce d\'identité',
    attestation_secu: 'Attestation sécurité sociale',
    justificatif_domicile: 'Justificatif de domicile',
    rib: 'RIB'
  };

  return (
    <div className="candidature-action-modal-overlay">
      <div className="candidature-action-modal">
        <Card className="candidature-action-modal-card">
          <CardHeader className="candidature-action-modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="candidature-action-modal-title">
                {getModalTitle()}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="candidature-action-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="candidature-action-modal-content">
            <div className="candidature-info-header">
              <h4 className="font-semibold text-primary mb-2">
                {candidature.prenom} {candidature.nom}
              </h4>
            </div>

            <form className="candidature-action-form">
              {actionType === 'accepte' && (
                <div className="form-group">
                  <p className="text-sm text-gray-700 mb-4">
                    Confirmer l'acceptation de cette candidature ?
                  </p>
                  <p className="text-xs text-gray-500">
                    Après acceptation, vous pourrez ajouter des documents supplémentaires via le bouton "Visualisé".
                  </p>
                </div>
              )}

              {actionType === 'refuse' && (
                <div className="form-group">
                  <Label htmlFor="raison_refus" className="form-label">
                    Raison du refus (optionnel)
                  </Label>
                  <Textarea
                    id="raison_refus"
                    value={formData.raison_refus}
                    onChange={(e) => handleInputChange('raison_refus', e.target.value)}
                    className="form-textarea"
                    placeholder="Expliquez brièvement la raison du refus..."
                    rows={3}
                  />
                </div>
              )}

              {actionType === 'visualiser' && (
                <div className="hs-documents-section">
                  <h5 className="font-semibold mb-4">Documents candidature existants</h5>
                  
                  {/* Existing documents */}
                  <div className="existing-documents mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      {candidature.cv_filename && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadFile(candidature.cv_filename)}
                          className="justify-start"
                        >
                          <Download size={16} className="mr-2" />
                          Télécharger CV
                        </Button>
                      )}
                      {candidature.lettre_filename && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadFile(candidature.lettre_filename)}
                          className="justify-start"
                        >
                          <Download size={16} className="mr-2" />
                          Télécharger Lettre
                        </Button>
                      )}
                    </div>
                  </div>

                  <h5 className="font-semibold mb-4">Ajouter des documents supplémentaires</h5>
                  
                  {/* HS Documents upload */}
                  <div className="hs-documents-grid">
                    {Object.keys(documentLabels).map((docType) => (
                      <div key={docType} className="form-group">
                        <Label htmlFor={docType} className="form-label">
                          {documentLabels[docType]}
                        </Label>
                        <Input
                          id={docType}
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                          onChange={(e) => handleFileChange(docType, e.target.files[0])}
                          className="form-input file-input"
                        />
                        {hsDocuments[docType] && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ {hsDocuments[docType].name}
                          </p>
                        )}
                        {candidature[`${docType}_filename`] && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleDownloadFile(candidature[`${docType}_filename`])}
                            className="p-0 h-auto text-xs text-blue-600 mt-1"
                          >
                            Document existant disponible
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="candidature-action-actions">
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary"
                  style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                >
                  <Check size={16} className="mr-2" />
                  {loading ? 'En cours...' : (
                    actionType === 'accepte' ? 'Accepter' :
                    actionType === 'refuse' ? 'Refuser' :
                    actionType === 'visualiser' ? 'Sauvegarder documents' : 'Confirmer'
                  )}
                </Button>
                <Button
                  onClick={onClose}
                  disabled={loading}
                  variant="outline"
                  className="btn-cancel"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CandidatureHSActionModal;